/* Single source of truth for route paths + sidebar structure.
   5 groups (Overview / Plan / Inspect / Follow-up / System), 15 screens.
   Each item's `id` is the module id in ROLES[].modules → per-role filtering.
   '/' = login. */

export type NavGroup = 'Overview' | 'Plan' | 'Inspect' | 'Follow-up' | 'System';

export interface NavItem {
  id: string; // module id (matches ROLES[].modules)
  text: string;
  path: string;
  icon: string; // icon key (see components/Icon)
  group: NavGroup;
}

export const LOGIN_PATH = '/';

export const NAV: NavItem[] = [
  // Overview
  { id: 'dashboard', text: 'Dashboard', path: '/dashboard', icon: 'grid', group: 'Overview' },
  { id: 'explorer', text: 'Bank Explorer', path: '/explorer', icon: 'tree', group: 'Overview' },
  // Plan
  { id: 'planning', text: 'Inspection Planning', path: '/planning', icon: 'calendar', group: 'Plan' },
  { id: 'masters', text: 'Organisation & Masters', path: '/masters', icon: 'tree', group: 'Plan' },
  { id: 'pre', text: 'Pre-Inspection Data', path: '/pre', icon: 'upload', group: 'Plan' },
  // Inspect. Advances, gold, cash, KYC and IT are STEPS of an open inspection,
  // reached from the workspace — not menu entries of their own. As menu items
  // they had no branch attached and each guessed at one.
  { id: 'inspect', text: 'Start Inspection', path: '/inspect/start', icon: 'clipboard', group: 'Inspect' },
  { id: 'inspect', text: 'Current Inspection', path: '/inspect/checklist', icon: 'clipboard', group: 'Inspect' },
  // Follow-up
  { id: 'obs', text: 'Observations', path: '/obs', icon: 'flag', group: 'Follow-up' },
  { id: 'scoring', text: 'Risk Scoring', path: '/scoring', icon: 'gauge', group: 'Follow-up' },
  { id: 'shg', text: 'SHG & Arrears', path: '/shg', icon: 'coins', group: 'Follow-up' },
  { id: 'reports', text: 'Reports', path: '/reports', icon: 'doc', group: 'Follow-up' },
  // System
  { id: 'admin', text: 'Administration', path: '/admin', icon: 'cog', group: 'System' },
];

export const GROUP_ORDER: NavGroup[] = ['Overview', 'Plan', 'Inspect', 'Follow-up', 'System'];

/** Path for a module id (used on role switch to jump to first allowed module). */
export const pathForModule = (moduleId: string): string =>
  NAV.find((n) => n.id === moduleId)?.path ?? '/dashboard';

/** Module id for a path (used to test whether a role may keep its route). */
export const moduleForPath = (path: string): string | undefined => {
  const exact = NAV.find((n) => n.path === path)?.id;
  if (exact !== undefined) {
    return exact;
  }
  // Every step under /inspect is the same module as the workspace itself, so a
  // role allowed to inspect is not locked out of the gold or cash step.
  return path.startsWith('/inspect') ? 'inspect' : undefined;
};

/* Which API module each page needs — the ONE place the web decides this.
 *
 * Derived by reading what every endpoint behind each page actually asks
 * RbacGuard for, not by guessing:
 *
 *   dashboard    Dashboard, Explorer, Dccbs, Reports, Reference/Datasets, Tnsc
 *   entities     Entities (masters), Branches (the branch book panel)
 *   inspections  Inspections, PreInspection, Gold, Sync, Evidence
 *   observations Observations
 *   scores       Scores
 *   admin        Settings writes; the Administration area itself is limited to
 *                SUPER_ADMIN / INSPECTION_HEAD / COMPLIANCE by requireAdmin(),
 *                which the server reports per role as `admin`.
 *
 * The sidebar and the Roles & Access tab both read this, so what the tab shows
 * is what the menu does. Before, the tab displayed a hard-coded list of
 * invented personas while the menu filtered on the same invented list, and
 * neither matched the matrix the API enforces.
 */
export const PAGE_REQUIRES: Record<string, string> = {
  dashboard: 'dashboard',
  explorer: 'dashboard',
  planning: 'inspections',
  masters: 'entities',
  pre: 'inspections',
  inspect: 'inspections',
  loans: 'inspections',
  gold: 'inspections',
  cash: 'inspections',
  kyc: 'inspections',
  it: 'inspections',
  obs: 'observations',
  scoring: 'scores',
  reports: 'dashboard',
  admin: 'admin',
};

/**
 * The page ids a set of API modules opens. '*' (SUPER_ADMIN) opens everything.
 * `isAdmin` comes from the server, because the Administration area is gated by
 * a role list rather than by the module matrix.
 */
export function pagesForModules(modules: string[], isAdmin = false): string[] {
  if (modules.includes('*')) {
    return Object.keys(PAGE_REQUIRES);
  }
  return Object.keys(PAGE_REQUIRES).filter((page) => {
    const needed = PAGE_REQUIRES[page];
    if (needed === 'admin') return isAdmin;
    return modules.includes(needed);
  });
}
